import { invert } from "lodash";

import {
  BurglarAlarmLabel,
  FortifiedHomeCertification,
  FuelTypeValue,
  UnitLevelUnderwritingAnswers,
} from "@covertree/types";
import {
  BurglarAlarmValue,
  IbhsFortifiedCertification,
  TypeOfFuelValue,
  UnderwritingAnswersDetailsInput,
  UnderwritingPolicyAnswersInput,
  UnderwritingQuestion,
  UnderwritingUnitAnswersInput,
  VisitorsInAMonthValue,
} from "@covertree/new-business-graphql";
import { yesNoToBoolean } from "@covertree/utils";

import { UnderwritingQuestionsForm } from "../validatorsAndTypes/underwritingQuestions";

export const mapFormKeyToUnderwritingQuestion: Record<
  keyof UnderwritingQuestionsForm,
  UnitLevelUnderwritingAnswers
> = {
  animalBite: UnitLevelUnderwritingAnswers.AnimalBite,
  automaticSprinkler: UnitLevelUnderwritingAnswers.AutomaticSprinkler,
  burglarAlarm: UnitLevelUnderwritingAnswers.BurglarAlarm,
  burglarAlarmValue: UnitLevelUnderwritingAnswers.BurglarAlarm,
  businessDescription: UnitLevelUnderwritingAnswers.BusinessDescription,
  businessEmployeesOnPremises:
    UnitLevelUnderwritingAnswers.BusinessEmployeesOnPremises,
  businessOnPremises: UnitLevelUnderwritingAnswers.BusinessOnPremises,
  daycareOnPremises: UnitLevelUnderwritingAnswers.DaycareOnPremises,
  visitorsInAMonth: UnitLevelUnderwritingAnswers.VisitorsInAMonth,
  conviction: UnitLevelUnderwritingAnswers.Conviction,
  cancellationRenew: UnitLevelUnderwritingAnswers.CancellationRenew,
  mortgage: UnitLevelUnderwritingAnswers.Mortgage,
  propertyWithFireProtection:
    UnitLevelUnderwritingAnswers.PropertyWithFireProtection,
  secureRails: UnitLevelUnderwritingAnswers.SecureRails,
  pleaseDescribe: UnitLevelUnderwritingAnswers.PleaseDescribe,
  sourceOfHeat: UnitLevelUnderwritingAnswers.SourceOfHeat,
  sourceOfHeatInstallation:
    UnitLevelUnderwritingAnswers.SourceOfHeatInstallation,
  typeOfFuel: UnitLevelUnderwritingAnswers.TypeOfFuel,
  divingBoard: UnitLevelUnderwritingAnswers.DivingBoard,
  divingBoardSlideLiabilityExtension:
    UnitLevelUnderwritingAnswers.DivingBoardSlideLiabilityExtension,
  fourFeetFence: UnitLevelUnderwritingAnswers.FourFeetFence,
  stormMitigationFortified:
    UnitLevelUnderwritingAnswers.StormMitigationFortified,
  stormMitigationFortifiedValue:
    UnitLevelUnderwritingAnswers.StormMitigationFortified,
  stormMitigationImpactglass:
    UnitLevelUnderwritingAnswers.StormMitigationImpactglass,
  stormMitigationShutters: UnitLevelUnderwritingAnswers.StormMitigationShutters,
  swimmingPool: UnitLevelUnderwritingAnswers.SwimmingPool,
  thermoStaticControl: UnitLevelUnderwritingAnswers.ThermoStaticControl,
  trampolineLiability: UnitLevelUnderwritingAnswers.TrampolineLiability,
  trampolineLiabilityExtension:
    UnitLevelUnderwritingAnswers.TrampolineLiabilityExtension,
  trampolineSafetyNet: UnitLevelUnderwritingAnswers.TrampolineSafetyNet,
  unitIsTied: UnitLevelUnderwritingAnswers.UnitIsTied,
  unrepairedDamages: UnitLevelUnderwritingAnswers.UnrepairedDamages,
  utilityServices: UnitLevelUnderwritingAnswers.UtilityServices,
  reasonCancellationRenew: UnitLevelUnderwritingAnswers.ReasonCancellationRenew,
  wroughtIron: UnitLevelUnderwritingAnswers.WroughtIron,
};

/** Questions keyed by the answer they define, as the UW components expect. */
export const toUnderwritingQuestionsObject = (
  questions: UnderwritingQuestion[]
) =>
  Object.fromEntries(
    questions.map((question) => [
      mapFormKeyToUnderwritingQuestion[
        question.fieldName as keyof UnderwritingQuestionsForm
      ],
      question,
    ])
  ) as Record<UnitLevelUnderwritingAnswers, UnderwritingQuestion>;

export const mapBusinessNumberVisitorsCountToEnum: Record<
  string,
  VisitorsInAMonthValue | string
> = {
  "0": VisitorsInAMonthValue.Zero,
  "1": VisitorsInAMonthValue.One,
  "2": VisitorsInAMonthValue.Two,
  "3": VisitorsInAMonthValue.Three,
  "4": VisitorsInAMonthValue.Four,
  "5": VisitorsInAMonthValue.Five,
  "6+": VisitorsInAMonthValue.SixOrMore,
};

export const mapBusinessEnumVisitorsCountToNumber = invert(
  mapBusinessNumberVisitorsCountToEnum
);

export function mapQuestionOptionsToFormOptions<
  I extends string,
  R extends string,
>(options?: I[] | null | undefined, labelMapping?: (value: I) => R) {
  return (options ?? []).map((value) => ({
    label: !!labelMapping ? labelMapping(value) : value,
    value,
  }));
}

export const mapTypeOfFuelValue = (value: string): TypeOfFuelValue =>
  value === FuelTypeValue.NotListed
    ? TypeOfFuelValue.NotListed
    : TypeOfFuelValue[value as TypeOfFuelValue];

export const mapIbhsFortifiedCertificationLabel = (
  value: IbhsFortifiedCertification
): FortifiedHomeCertification => {
  switch (value) {
    case IbhsFortifiedCertification.Bronze:
      return FortifiedHomeCertification.Bronze;
    case IbhsFortifiedCertification.SaferLiving:
      return FortifiedHomeCertification.SaferLiving;
    case IbhsFortifiedCertification.Silver:
      return FortifiedHomeCertification.Silver;
    case IbhsFortifiedCertification.Gold:
      return FortifiedHomeCertification.Gold;
    default:
      return FortifiedHomeCertification.No;
  }
};

export const mapBurglarAlarmLabel = (
  value: BurglarAlarmValue
): BurglarAlarmLabel => {
  switch (value) {
    case BurglarAlarmValue.Local:
      return BurglarAlarmLabel.Local;
    case BurglarAlarmValue.Monitored:
      return BurglarAlarmLabel.Monitored;
    default:
      return BurglarAlarmLabel.No;
  }
};

export const mapUnderwritingQuestionsFormToQueryInput = (
  mainUnitExposureId: string,
  form: UnderwritingQuestionsForm,
  isTenantFlow: boolean,
  questions: Record<UnitLevelUnderwritingAnswers, UnderwritingQuestion>
): {
  policyLevelUW: UnderwritingPolicyAnswersInput;
  unitLevelUW: UnderwritingUnitAnswersInput[];
} => {
  const policyLevelAnswers: UnderwritingPolicyAnswersInput = {
    animalBite: yesNoToBoolean(form.animalBite),
    conviction: yesNoToBoolean(form.conviction),
    cancellationRenew: {
      cancellationRenew: yesNoToBoolean(form.cancellationRenew),
      reasonCancellationRenew: form.reasonCancellationRenew,
    },
  };

  const unitLevelAnswersForTenant: UnderwritingAnswersDetailsInput = {
    automaticSprinkler: yesNoToBoolean(form.automaticSprinkler),
    propertyWithFireProtection: yesNoToBoolean(form.propertyWithFireProtection),
    businessOnPremises: yesNoToBoolean(form.businessOnPremises),
    daycareOnPremises: yesNoToBoolean(form.daycareOnPremises),
    businessEmployeesOnPremises: yesNoToBoolean(
      form.businessEmployeesOnPremises
    ),
    visitorsInAMonth:
      form.visitorsInAMonth !== "Zero"
        ? VisitorsInAMonthValue[form.visitorsInAMonth as VisitorsInAMonthValue]
        : undefined,
    businessDescription: form.businessDescription,
    stormMitigationImpactglass: yesNoToBoolean(form.stormMitigationImpactglass),
    stormMitigationShutters: yesNoToBoolean(form.stormMitigationShutters),
  };

  const hasUnderwritingQuestion = (fieldName: UnitLevelUnderwritingAnswers) =>
    questions?.[fieldName] !== undefined;

  const unitLevelAnswers = {
    ...unitLevelAnswersForTenant,
    burglarAlarm:
      BurglarAlarmValue[
        (form.burglarAlarmValue as BurglarAlarmValue) ?? BurglarAlarmValue.No
      ],
    cancellationRenew: yesNoToBoolean(form.cancellationRenew),
    divingBoard: yesNoToBoolean(form.divingBoard),
    divingBoardSlideLiabilityExtension: hasUnderwritingQuestion(
      UnitLevelUnderwritingAnswers.DivingBoardSlideLiabilityExtension
    )
      ? yesNoToBoolean(form.divingBoardSlideLiabilityExtension)
      : undefined,
    fourFeetFence: yesNoToBoolean(form.fourFeetFence),
    mortgage: yesNoToBoolean(form.mortgage),
    pleaseDescribe: form.pleaseDescribe,
    reasonCancellationRenew: form.reasonCancellationRenew,
    secureRails: yesNoToBoolean(form.secureRails),
    sourceOfHeat: yesNoToBoolean(form.sourceOfHeat),
    sourceOfHeatInstallation: yesNoToBoolean(form.sourceOfHeatInstallation),
    stormMitigationFortified:
      IbhsFortifiedCertification[
        form.stormMitigationFortifiedValue as IbhsFortifiedCertification
      ],
    swimmingPool: yesNoToBoolean(form.swimmingPool),
    thermoStaticControl: yesNoToBoolean(form.thermoStaticControl),
    trampolineLiability: yesNoToBoolean(form.trampolineLiability),
    trampolineLiabilityExtension: yesNoToBoolean(
      form.trampolineLiabilityExtension
    ),
    trampolineSafetyNet: yesNoToBoolean(form.trampolineSafetyNet),
    typeOfFuel: TypeOfFuelValue[form.typeOfFuel as TypeOfFuelValue],
    unitIsTied: yesNoToBoolean(form.unitIsTied),
    unrepairedDamages: yesNoToBoolean(form.unrepairedDamages),
    utilityServices: yesNoToBoolean(form.utilityServices),
    wroughtIron: yesNoToBoolean(form.wroughtIron),
  };

  return {
    policyLevelUW: policyLevelAnswers,
    unitLevelUW: [
      {
        exposureId: mainUnitExposureId,
        answers: isTenantFlow ? unitLevelAnswersForTenant : unitLevelAnswers,
      },
    ],
  };
};
